import React, { useState, useEffect, useRef } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { ButtonGroup } from 'components/ui/button'
import { FieldSection } from 'components/ui/fieldset'
import { FooterEditEntity } from 'components/ui/footer'
import { Input } from 'components/ui/input'
import { MediaField } from 'domains/media/components/media-field'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { tourSchema } from 'domains/org/getty/schemas/tour'
import { GettyStore } from 'domains/org/getty/store'
import { getValidationWarning } from 'domains/org/getty/utils/validation'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { Page } from 'generated/mos/pagination'
import { GetTourRequest } from 'generated/mos/org/getty/audioaccess'
import { Media } from 'generated/mos/media'
import { Tour } from 'generated/mos/org/getty/audio'
import { UpdateTourRequest } from 'generated/mos/org/getty/audiomanagement'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { convertEntityToFormValues } from 'helpers/schema'
import { statusSelector, Status } from 'helpers/status'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { getTourSubnav } from '../tour-update/subnav'
import { TourDelete } from '../tour-delete'
import { ENVIRONMENT, appEnvironment } from 'domains/org/getty/utils/environment'
import { baseDeepLinkDomain, gettyCenterSiteId, gettyVillaSiteId } from 'helpers/deep-links'

type FormValues = {
  name: string | undefined;
  description?: string | undefined;
  webpageUrl?: string | undefined;
  durationMinutes: number | undefined;
  imageAltText: string | undefined;
  creditText: string | undefined;
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetTourRequest' | 'gettyUpdateTourRequest' | 'gettyUpdateTourReset' | 'gettyListValidationFailuresRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'tourUpdate' | 'validationFailuresList'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly tourId: string;
}

type Props = ActionProps & ConnectedProps & DirectProps


const statusSummary = (failures: {issues: ReadonlyArray<ValidationFailure.Issue.Entity>} | undefined) => {
  const warning: Array<{text: string}> = [];
  const failureFields = failures && failures.issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'MISSING_REQUIRED_ATTRIBUTE')
  if (failureFields && !!failureFields.length) {
    warning.push({ text: `The following fields are required to publish this tour: ${failureFields.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.` });
  }
  if (failures && failures.issues.find(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'TOUR_CONTAINS_STOPS_AND_HAS_WEBPAGE_URL')) {
    warning.push({ text: "Tour contains stops and has a webpage URL" });
  }

  if (warning.length) {
    return function renderStatusSummary() {
      return <StatusSummary warning={warning} />
    }
  } else {
    return undefined
  }
}

const TourUpdateDetailsView = (props: Props) => {
  const {
    gettyGetTourRequest,
    gettyUpdateTourRequest,
    gettyUpdateTourReset,
    historyPush,
    projectId,
    toastNotification,
    tourId,
    tourUpdate,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
    gettyListValidationFailuresRequest,
    validationFailuresList,
  } = props

  // set up form values
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ imageMedia, setImageMedia ] = useState<Media.Ref | undefined>(undefined)

  const [deleteTourModalOpen, setDeleteTourModalOpen] = React.useState(false)

  const backLocation = `/project/${projectId}/tours`

  const tourUpdateData = statusSelector.data(tourUpdate)
  const tour = tourUpdateData && tourUpdateData.tour
  const validationFailuresListData = statusSelector.data(validationFailuresList)

  const tourRef = { typename: Tour.refName, id: props.tourId }

  const hasSubmitted = useRef(false)

  const siteId = tour && tour.siteRef && tour.siteRef.id
  const deepLinkUrl = `${baseDeepLinkDomain}?deeplink=site/${siteId}/tour/${tourId}`

  useEffect(() => {
    // once the entity is ready, initialise the form values, or reinitialise once an update was
    // successful.
    if (tour && initialFormValues && tourUpdate.status === Status.Ready) {
      setFormBag(createFormBag(initialFormValues))
      setImageMedia(initialImageValue)
    }

    if (tourUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([{
        typename: Tour.refName,
        id: tourId
      }])
      toastNotification({ type: 'success', text: 'Tour updated' })
      hasSubmitted.current = false
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs: [{
            typename: Tour.refName,
            id: tourId,
          }]
        }
      })
    }

  }, [tourUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // load initial data
  if (tourUpdate.status === Status.Idle || (tour && Tour.mustRef(tour.ref).id !== tourId)) {
    gettyGetTourRequest({
      type: GetTourRequest.refName,
      tourRef: {
        typename: Tour.refName,
        id: tourId
      }
    })
    releaseControlsListReleaseControlsRequest([{
      typename: Tour.refName,
      id: tourId
    }])
    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [{
          typename: Tour.refName,
          id: tourId,
        }]
      }
    })
    return null;
  }

  const tourValues: Partial<Tour.Entity> = pick({
    ...Tour.defaults,
    ...tour
  }, 'name', 'description', 'webpageUrl', 'durationMinutes', 'imageAltText', 'creditText');

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = tour ?
    convertEntityToFormValues<Tour.Entity>(tourValues, tourSchema) as any as FormValues:
    undefined;
  const initialImageValue = tour && tourSchema.imageRef.read(tour.imageRef) as any as Media.Ref | undefined;


  // handle the form ui.
  const modified = initialFormValues && formBag && tour && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialImageValue, imageMedia)
  );

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};

    if (!values.name || !values.name.trim().length) {
      errors.name = 'Tour name required';
    }

    if (
      values.durationMinutes &&  // there is a value present
      !Number.isInteger(Number(values.durationMinutes)) || Number(values.durationMinutes) < 0) {   // the value is not a number or the value is a negative number
      errors.durationMinutes = 'Please enter a valid number';
    }

    // if the parsing returns NaN it will return undefined to pass modified check
    // otherwise it will parse the integer
    isNaN(parseInt(String(values.durationMinutes)))
      ? values.durationMinutes = undefined
      : values.durationMinutes = parseInt(String(values.durationMinutes))

    return errors;
  };

  const submit = (formBag: FormBag<FormValues>, tour: Tour.Entity) => {
    const bag = validateFormBag(formBag, validateForm);
    setFormBag(bag);

    if (!bag.valid) {
      return false;
    }

    const entityValues = Object.entries(formBag.values).reduce((accumulator, currentValue) => {
      const key = currentValue[0] as keyof Tour.Entity;
      const value = currentValue[1];
      return {
        ...accumulator,
        [key]: tourSchema[key] ? tourSchema[key].write(value, tour[key]) : value
      };
    }, {});

    gettyUpdateTourRequest({
      ...UpdateTourRequest.defaults,
      tourRef: Tour.mustRef(tour.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'name',
          'description',
          'durationMinutes',
          'imageRef',
          'imageAltText',
          'creditText',
          'webpageUrl',
        ]
      },
      ...entityValues,
      imageRef: imageMedia ? tourSchema.imageRef ? tourSchema.imageRef.write(imageMedia) : imageMedia : undefined,
    })

    hasSubmitted.current = true

    return false
  };

  // if the status is stale, trigger failure toast
  if (tourUpdate.status === Status.Stale) {
    gettyUpdateTourReset()
    toastNotification({ type: 'error', text: 'Can’t save because of an unknown error' })
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    tour && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: tourRef,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.id === tourId) : undefined
  const failureFields = failures && failures.issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'MISSING_REQUIRED_ATTRIBUTE' || issueType === 'TOUR_CONTAINS_STOPS_AND_HAS_WEBPAGE_URL')

  return (
    <>
      {deleteTourModalOpen && (
        <TourDelete
          onCancel={() => setDeleteTourModalOpen(false)}
          tourRef={tourRef}
          projectId={props.projectId}
          historyPush={historyPush}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit tour"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={tour && tour.name && tourSchema.name ? tourSchema.name.read(tour.name) as string : '' }
        toolbar={() => (
          <ButtonGroup>
            {tour &&
              <ReleaseControlUpdate
                hasBorder
                entityRef={tourRef}
                updateHandler={releaseControlUpdate}
              />
            }
            <PopOverMenu hasBorder={true}>
              <PopOverMenuButton onClick={() => setDeleteTourModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        subnav={() => (
          <SecondaryNavigation
            navItems={getTourSubnav(projectId, tourId)}
            modified={modified}
            historyPush={historyPush}
          />
        )}
        summary={statusSummary(failures)}
        content={() => (
          <StatusGuard status={tourUpdate} mode="form">
            {formBag && tour && (
              <form noValidate onSubmit={(event) => { event.preventDefault() }}>
                <FormData
                  bag={formBag}
                  onUpdate={(event) => {
                    setFormBag(event.bag)
                  }}
                  validate={() => ({})}
                >
                  <Input
                    label="Tour name"
                    field="name"
                    error={formBag.touched.name ? formBag.errors.name : undefined}
                    warning={getValidationWarning<Tour.Entity>('name', failureFields)}
                  />
                  <FieldSection />
                  <Input
                    label="Webpage URL"
                    field="webpageUrl"
                    error={formBag.touched.webpageUrl ? formBag.errors.webpageUrl : undefined}
                    warning={getValidationWarning<Tour.Entity>('webpageUrl', failureFields)}
                  />
                  <Input
                    component="textarea"
                    label="Description"
                    field="description"
                    warning={getValidationWarning<Tour.Entity>('description', failureFields)}
                  />
                  <Input
                    label="Duration"
                    field="durationMinutes"
                    labelSupportingText="Enter an approximate time in minutes."
                    error={formBag.touched.durationMinutes ? formBag.errors.durationMinutes as string : undefined}
                    warning={getValidationWarning<Tour.Entity>('durationMinutes', failureFields)}
                  />
                  <FieldSection />
                  <MediaField
                    kind="MEDIA_KIND_RASTER_IMAGE"
                    mediaRef={imageMedia}
                    onUpdate={(entity) => { setImageMedia(entity ? entity.ref : undefined) }}
                    label="Tour tile Image"
                    warning={getValidationWarning<Tour.Entity>('imageRef', failureFields)}
                  />
                  <Input
                    label="Alternate Text"
                    description="Description of what the image contains if it cannot be shown."
                    field="imageAltText"
                    isAssociated={true}
                    warning={getValidationWarning<Tour.Entity>('imageAltText', failureFields)}
                  />
                  <Input
                    label="Credit"
                    description="Credit the author of the photo or object."
                    field="creditText"
                    isAssociated={true}
                    warning={getValidationWarning<Tour.Entity>('creditText', failureFields)}
                  />
                </FormData>
              </form>
            )}
            <section>
              <h4>Deep Link</h4>
              <a href={deepLinkUrl} target="_blank" rel="noopener noreferrer">
                {deepLinkUrl}
              </a>
            </section>
          </StatusGuard>
        )}
        sidebar={() => null}
        footer={() => formBag && tour && (
          <FooterEditEntity
            entity={tourUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBag, tour);
              return false;
            }}
          />
        )}
      />
    </>
  )
}

export const TourUpdateDetails = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => ({
    ...pick(store.getty, 'tourUpdate', 'validationFailuresList'),
  }),
  {
    ...pick(actionCreators, 'gettyGetTourRequest', 'gettyUpdateTourRequest', 'gettyUpdateTourReset', 'gettyListValidationFailuresRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(TourUpdateDetailsView);
